import { memo, useMemo } from 'react';
import { Milestone } from '../milestones/milestone';
import { Phase } from './phase';
import { MARGIN_BETWEEN_ROWS, TIMELINES_ITEM_ROW_HEIGHT } from '../consts';
import styled from 'styled-components';
import {
  ItemTops,
  PhasesRowWithRenderingParameters,
  TimelinePhasesWithRenderingParameters,
  TimelineViewCommands,
} from '../../types';
import { ChartDimensions } from '@ardoq/graph';
import { TimelinesGridPrecisionUnit } from '@ardoq/api-types';
import { getRenderedItemPhases } from './calculations';
import { getRowHeightFromItemTops } from '../../calculations';

const ItemBackground = styled.div`
  position: absolute;
  top: -4px;
  border-width: 1px 0px;
  width: 100%;
  z-index: 0;
  opacity: 0.7;
`;

const ItemPhasesContainer = styled.div`
  width: 100%;
  height: ${TIMELINES_ITEM_ROW_HEIGHT}px;
  min-width: 0px;
  display: block;
  position: relative;
`;

const RowsContainer = styled.div`
  pointer-events: auto;
  position: absolute;
  z-index: 3;
  margin-bottom: ${MARGIN_BETWEEN_ROWS}px;
`;

const PhasesOverflowContainer = styled.div`
  overflow: hidden;
  position: relative;
  height: 100%;
  width: 100%;
`;

type PhasesRowProps = {
  itemPhasesRow: PhasesRowWithRenderingParameters;
  itemId: string;
  modelId: string;
  backgroundColor: string | null;
  rowHeight: number;
  chartDimensions: ChartDimensions;
  contextMenuDataAttribute: string;
  gridPrecision: TimelinesGridPrecisionUnit;
  isLastItem: boolean;
  viewInstanceId: string;
  isExcluded: boolean;
  phasesRowIndex: number;
  commands: TimelineViewCommands;
};
const PhasesRow = ({
  itemPhasesRow,
  itemId,
  modelId,
  chartDimensions: {
    margin: { left: chartLeft },
    chart: { width: chartWidth },
  },
  contextMenuDataAttribute,
  gridPrecision,
  isLastItem,
  viewInstanceId,
  backgroundColor,
  rowHeight = TIMELINES_ITEM_ROW_HEIGHT,
  isExcluded,
  phasesRowIndex,
  commands,
}: PhasesRowProps) => {
  const setHoveredItem = (nodeId: string | null) =>
    commands.setHoveredItem(nodeId, viewInstanceId);

  return (
    <ItemPhasesContainer
      style={{
        ...(isExcluded ? { opacity: 0.2 } : undefined),
      }}
      onMouseLeave={() => {
        if (isLastItem) {
          setHoveredItem(null);
        }
      }}
      onMouseEnter={() => setHoveredItem(itemId)}
      onClick={() => commands.setFocusedItem(modelId, viewInstanceId)}
      {...(contextMenuDataAttribute
        ? { [contextMenuDataAttribute]: modelId }
        : {})}
    >
      {!phasesRowIndex ? (
        <ItemBackground
          style={{
            left: 0 - chartLeft,
            width: chartLeft + chartWidth,
            backgroundColor: backgroundColor ?? 'transparent',
            height: rowHeight + 8,
          }}
        />
      ) : null}

      <PhasesOverflowContainer>
        {itemPhasesRow.phases.map((phase, phaseIndex) => {
          return (
            <Phase
              key={`${itemId}_${phaseIndex}_${phase.name}`}
              model={phase}
              graphNodeId={itemId}
              infiniteLeft={phase.infiniteLeft}
              infiniteRight={phase.infiniteRight}
              dynamicPhaseStyles={phase.dynamicPhaseStyles}
              modelId={modelId}
              index={phaseIndex}
              gridPrecision={gridPrecision}
              phasesRowIndex={phasesRowIndex}
            />
          );
        })}
        {itemPhasesRow.milestones.map((milestone, index) => (
          <Milestone
            key={`${itemId}_${milestone.label}_${index}`}
            milestone={milestone}
            graphNodeId={itemId}
            phasesRowIndex={phasesRowIndex}
          />
        ))}
      </PhasesOverflowContainer>
    </ItemPhasesContainer>
  );
};

type ItemPhasesArgs = {
  itemPhases: TimelinePhasesWithRenderingParameters;
  chartDimensions: ChartDimensions;
  contextMenuDataAttribute: string;
  gridPrecision: TimelinesGridPrecisionUnit;
  isLastItem: boolean;
  viewInstanceId: string;
  containerWidth: number;
  renderedItemTops: ItemTops;
  rowIndex: number;
  commands: TimelineViewCommands;
};

export const ItemPhases = memo(
  ({
    itemPhases,
    chartDimensions,
    contextMenuDataAttribute,
    gridPrecision,
    isLastItem,
    viewInstanceId,
    renderedItemTops,
    rowIndex,
    containerWidth,
    commands,
  }: ItemPhasesArgs) => {
    const {
      groupSeparatorStyle,
      backgroundColor,
      itemModel: { modelId, id: itemId, excluded },
    } = itemPhases;

    const renderedPhases = useMemo(
      () => getRenderedItemPhases(itemPhases),
      [itemPhases]
    );

    const { rowHeight, rowTop } = useMemo(
      () => ({
        rowHeight: getRowHeightFromItemTops(renderedItemTops, rowIndex),
        rowTop: renderedItemTops[rowIndex],
      }),
      [renderedItemTops, rowIndex]
    );

    return (
      <RowsContainer
        style={{
          height: rowHeight,
          width: containerWidth,
          ...groupSeparatorStyle,
          top: rowTop,
        }}
      >
        {renderedPhases?.map((phasesRow, index) => (
          <PhasesRow
            key={`${itemId}_${index}`}
            itemPhasesRow={phasesRow}
            phasesRowIndex={index}
            itemId={itemId}
            modelId={modelId}
            chartDimensions={chartDimensions}
            contextMenuDataAttribute={contextMenuDataAttribute}
            gridPrecision={gridPrecision}
            isLastItem={isLastItem}
            viewInstanceId={viewInstanceId}
            backgroundColor={backgroundColor}
            isExcluded={excluded}
            rowHeight={rowHeight}
            commands={commands}
          />
        ))}
      </RowsContainer>
    );
  }
);
